import type { ReactNode } from 'react';
import { ExternalRefChip } from '@reflex/ui/components/shared/ExternalRefChip';
import { externalRefProfileFor } from '@reflex/plugin-api/external-refs';
import type { FlowTriggerExternalRefs } from '@reflex/plugin-api';
import { providerDisplayLabel } from '../../shared/external-refs.js';
import type { SchemaExternalRefs } from '../useExternalRefs.js';

/**
 * One payload value (a provider entity id, or a list of them) rendered as
 * resolved reference badges. Non-string entries are ignored and repeats
 * are collapsed — payloads are webhook data, not trusted shapes.
 */
export function RefChips({
  value,
  provider,
  kind,
  refs,
}: {
  value: unknown;
  provider: string;
  kind: string;
  refs: FlowTriggerExternalRefs;
}) {
  const ids = [
    ...new Set(
      (Array.isArray(value) ? value : [value]).filter(
        (entry): entry is string => typeof entry === 'string' && entry.length > 0,
      ),
    ),
  ];
  if (ids.length === 0) return null;
  const label = providerDisplayLabel(provider);
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="ref-chips">
      {ids.map((id) => (
        <ExternalRefChip
          key={id}
          id={id}
          providerLabel={label}
          kindLabel={kind}
          profile={externalRefProfileFor(refs, id)}
          pending={refs.pending}
          // Payload key/value lists render 12px values; match that rhythm.
          size="md"
        />
      ))}
    </span>
  );
}

/**
 * Per-key label hints for `StructuredPayloadView`: reference fields carry
 * the schema's friendly `title` ('Assignee'), matching the conditions
 * editor's labels instead of the auto-formatted key ('Assignee Id').
 */
export function externalRefLabelHints(
  refs: SchemaExternalRefs | undefined,
): Record<string, string> | undefined {
  if (!refs) return undefined;
  const hints: Record<string, string> = {};
  for (const field of refs.fields) {
    if (field.title) hints[field.key] = field.title;
  }
  return Object.keys(hints).length > 0 ? hints : undefined;
}

/**
 * Per-key value overrides for `StructuredPayloadView`: every
 * reference-typed field that holds an id renders as badges instead of a
 * raw string. Undefined when there is nothing to decorate, so callers can
 * pass the result straight through.
 */
export function buildExternalRefOverrides(
  refs: SchemaExternalRefs | undefined,
  payload: Record<string, unknown> | null | undefined,
): Record<string, ReactNode> | undefined {
  if (!refs || !payload) return undefined;
  const overrides: Record<string, ReactNode> = {};
  for (const field of refs.fields) {
    const value = payload[field.key];
    const hasId = Array.isArray(value)
      ? value.some((entry) => typeof entry === 'string' && entry.length > 0)
      : typeof value === 'string' && value.length > 0;
    if (!hasId) continue;
    overrides[field.key] = (
      <RefChips value={value} provider={field.provider} kind={field.kind} refs={refs} />
    );
  }
  return Object.keys(overrides).length > 0 ? overrides : undefined;
}
